import { v4 as uuid } from 'uuid';

type SlateNode = {
  type?: string;
  id?: string;
  text?: string;
  children?: SlateNode[];
};

const paragraphNode = (text: string): SlateNode => ({
  type: 'paragraph',
  id: uuid(),
  children: [{ text }],
});

const isSlateNodeArray = (value: unknown): value is SlateNode[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((node) => typeof node === 'object' && node !== null && Array.isArray((node as SlateNode).children));

// List containers keep one item per line in the extracted plain text.
const LIST_TYPES = new Set(['bulleted-list', 'ordered-list']);

const nodeToPlainText = (node: SlateNode): string => {
  if (typeof node.text === 'string') return node.text;

  const children = node.children ?? [];
  return children.map(nodeToPlainText).join(LIST_TYPES.has(node.type ?? '') ? '\n' : '');
};

/**
 * The journey description fields historically store plain text entered via a textarea,
 * while CustomEditorV2 expects a stringified Slate node array. Legacy plain-text values
 * are wrapped into paragraph nodes (one per line) so they open correctly in the editor.
 */
export const toRichTextEditorValue = (value: string): string => {
  if (!value) {
    return JSON.stringify([paragraphNode('')]);
  }

  try {
    if (isSlateNodeArray(JSON.parse(value))) {
      return value;
    }
  } catch {
    // Not JSON — legacy plain text.
  }

  return JSON.stringify(value.split('\n').map(paragraphNode));
};

/**
 * Extracts plain text from a stored description value — Slate JSON or legacy plain text.
 * Used for closed-state previews and for judging whether rich content is actually empty.
 */
export const richTextToPlainText = (value: string): string => {
  if (!value) return '';

  try {
    const parsed = JSON.parse(value);
    if (isSlateNodeArray(parsed)) {
      return parsed.map(nodeToPlainText).join('\n').trim();
    }
    if (Array.isArray(parsed)) {
      // An empty (or non-Slate) array is empty content, not legacy plain text.
      return '';
    }
  } catch {
    // Legacy plain text.
  }

  return value;
};

/**
 * The V2 description fields travel as a GraphQL JSON scalar — a parsed Slate node array,
 * not a string. Editor and display components work with strings, so serialize here.
 * Content that is effectively empty becomes '' to keep hide-empty-section checks working.
 */
export const richTextJsonToString = (value: unknown): string => {
  if (!value) return '';

  const serialized = typeof value === 'string' ? value : JSON.stringify(value);
  return richTextToPlainText(serialized) ? serialized : '';
};

/**
 * Editor output (stringified Slate nodes) → JSON scalar input for the V2 fields.
 * The backend rejects null for these fields, so effectively empty content is stored
 * as the canonical empty document (a single empty paragraph — the editor's own empty
 * state, which the read-side helpers already treat as empty content); anything else
 * is normalized to a Slate node array.
 */
export const toStoredRichTextJson = (editorValue: string): unknown => {
  const isEmpty = !richTextToPlainText(editorValue);
  return JSON.parse(toRichTextEditorValue(isEmpty ? '' : editorValue));
};
